import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/shared/Icon';
import { searchFtueCatalog } from './catalog';
import { FtueBlueprint } from './diagrams';
import {
  ensureAnchorVisible,
  findFtueAnchor,
  FTUE_SPOTLIGHT_MAX_VH,
  inflateRect,
  measureAnchor,
  placeCallout,
  scrollFtueBackdrop,
  type CalloutLayout,
  type Rect,
} from './geometry';
import { requestMaitreIntro } from '../components/assistant/maitreIntroState';
import {
  resolveFtueJourneyLabel,
  resolveFtueSurfaceLabel,
} from './registry';
import type { FtueController } from './useFtueController';

export function FtueOverlay({
  ftue,
  onAskStarter,
  portalHost,
  appRoot,
}: {
  ftue: FtueController;
  onAskStarter?: (prompt: string) => void;
  portalHost?: HTMLElement | null;
  appRoot?: HTMLElement | null;
}) {
  const active = ftue.phase !== 'idle';
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasActiveRef = useRef(false);

  if (
    active &&
    !wasActiveRef.current &&
    typeof document !== 'undefined'
  ) {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  }
  wasActiveRef.current = active;

  useEffect(() => {
    if (!active) return;
    return () => {
      const previousFocus = previousFocusRef.current;
      previousFocusRef.current = null;
      queueMicrotask(() => {
        if (previousFocus?.isConnected) {
          try {
            previousFocus.focus({ preventScroll: true });
          } catch {
            previousFocus.focus();
          }
        }
      });
    };
  }, [active]);

  useEffect(() => {
    if (!active || !appRoot) return;

    const previousInert = appRoot.inert;
    const previousAriaHidden = appRoot.getAttribute('aria-hidden');
    appRoot.inert = true;
    appRoot.setAttribute('aria-hidden', 'true');

    return () => {
      appRoot.inert = previousInert;
      if (previousAriaHidden == null) appRoot.removeAttribute('aria-hidden');
      else appRoot.setAttribute('aria-hidden', previousAriaHidden);
    };
  }, [active, appRoot]);

  if (ftue.phase === 'idle') return null;
  if (typeof document === 'undefined') return null;

  const showSpotlight =
    ftue.phase === 'tour' ||
    (ftue.phase === 'contextual' && !!ftue.activeTarget?.anchor);
  const showHelp =
    ftue.phase === 'contextual' || ftue.phase === 'catalog';

  return createPortal(
    <div className="ftue-root" data-ftue-phase={ftue.phase}>
      {(ftue.phase === 'welcome' || ftue.phase === 'whats-new') && (
        <FtueWelcomeCard ftue={ftue} />
      )}
      {showSpotlight && (
        <FtueSpotlightLayer ftue={ftue} onAskStarter={onAskStarter} />
      )}
      {/* One dialog at a time: hide help while a spotlight/callout is active. */}
      {showHelp && !showSpotlight && <FtueHelpPanel ftue={ftue} />}
    </div>,
    portalHost ?? document.body,
  );
}

function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;
    const root = containerRef.current;
    if (!root) return;

    const focusable = () =>
      Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !root.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (
        document.activeElement === last ||
        !root.contains(document.activeElement)
      ) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, containerRef]);
}

function FtueWelcomeCard({ ftue }: { ftue: FtueController }) {
  const { t } = useTranslation('ftue');
  const isWhatsNew = ftue.phase === 'whats-new';
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useFocusTrap(dialogRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (isWhatsNew) ftue.dismissWhatsNewInvite();
        else ftue.skipTour();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ftue, isWhatsNew]);

  return (
    <div className="ftue-scrim" role="presentation">
      <div
        ref={dialogRef}
        className="ftue-welcome"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ftue-welcome-title"
        aria-describedby="ftue-welcome-desc"
        tabIndex={-1}
      >
        <FtueBlueprint kind="performance-story" className="ftue-welcome-bp" />
        <h2 id="ftue-welcome-title">
          {isWhatsNew ? t('overlay.whatsNewTitle') : t('overlay.welcomeTitle')}
        </h2>
        <p id="ftue-welcome-desc">
          {isWhatsNew ? t('overlay.whatsNewBody') : t('overlay.welcomeBody')}
        </p>
        <div className="ftue-welcome-actions">
          <button
            type="button"
            className="ftue-btn primary"
            onClick={() =>
              ftue.startFullTour(isWhatsNew ? 'whats-new' : 'auto')
            }
          >
            {isWhatsNew ? t('overlay.showMe') : t('overlay.start')}
          </button>
          <button
            type="button"
            className="ftue-btn ghost"
            onClick={() =>
              isWhatsNew ? ftue.dismissWhatsNewInvite() : ftue.skipTour()
            }
          >
            {isWhatsNew ? t('overlay.notNow') : t('overlay.skip')}
          </button>
        </div>
        <p className="ftue-welcome-hint">{t('overlay.welcomeHint')}</p>
      </div>
    </div>
  );
}

function FtueSpotlightLayer({
  ftue,
  onAskStarter,
}: {
  ftue: FtueController;
  onAskStarter?: (prompt: string) => void;
  suppressHelp?: boolean;
}) {
  const { t } = useTranslation('ftue');
  const target = ftue.activeTarget;
  const [hole, setHole] = useState<Rect | null>(null);
  const [layout, setLayout] = useState<CalloutLayout | null>(null);
  const [anchorUnavailable, setAnchorUnavailable] = useState(false);
  const calloutRef = useRef<HTMLDivElement>(null);
  const touchYRef = useRef<number | null>(null);
  const isTour = ftue.phase === 'tour';

  useFocusTrap(calloutRef, true);

  useLayoutEffect(() => {
    if (!target?.anchor) {
      setHole(null);
      setAnchorUnavailable(false);
      return;
    }
    ensureAnchorVisible(target.anchor);

    let frameId: number | null = null;
    const measure = () => {
      const maxHeight = Math.round(window.innerHeight * FTUE_SPOTLIGHT_MAX_VH);
      const raw = measureAnchor(target.anchor, { maxHeight });
      if (!raw) {
        setHole(null);
        setAnchorUnavailable(true);
        return;
      }
      setAnchorUnavailable(false);
      const inflated = inflateRect(raw);
      setHole(inflated);
      const size = calloutRef.current?.getBoundingClientRect();
      // Prefer above for bottom-of-page targets so the table stays readable.
      const preferred =
        inflated.top > window.innerHeight * 0.55 ? 'above' : 'below';
      setLayout(
        placeCallout(
          inflated,
          { width: window.innerWidth, height: window.innerHeight },
          preferred,
          size
            ? { width: size.width || 320, height: size.height || 220 }
            : undefined,
        ),
      );
    };
    const scheduleMeasure = () => {
      if (frameId != null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        measure();
      });
    };

    measure();
    // Smooth scroll settles asynchronously — remasure after it lands.
    const settleId = window.setTimeout(() => {
      ensureAnchorVisible(target.anchor);
      scheduleMeasure();
    }, 320);
    const ro =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(scheduleMeasure);
    const anchor = findFtueAnchor(target.anchor);
    if (anchor) ro?.observe(anchor);
    if (calloutRef.current) ro?.observe(calloutRef.current);
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('scroll', scheduleMeasure, true);
    return () => {
      ro?.disconnect();
      window.clearTimeout(settleId);
      if (frameId != null) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('scroll', scheduleMeasure, true);
    };
  }, [target?.anchor, target?.id, ftue.stepIndex, ftue.journeyId]);

  const onBackdropWheel = (e: React.WheelEvent) => {
    // Block strips sit above the canvas; forward wheel so operators can still
    // bring tall targets (outlet table) into the spotlight band.
    e.preventDefault();
    scrollFtueBackdrop(target?.anchor, e.deltaY);
  };
  const onBackdropTouchStart = (e: React.TouchEvent) => {
    touchYRef.current = e.touches[0]?.clientY ?? null;
  };
  const onBackdropTouchMove = (e: React.TouchEvent) => {
    const nextY = e.touches[0]?.clientY;
    const previousY = touchYRef.current;
    if (nextY == null || previousY == null) return;
    e.preventDefault();
    touchYRef.current = nextY;
    scrollFtueBackdrop(target?.anchor, previousY - nextY);
  };
  const onBackdropTouchEnd = () => {
    touchYRef.current = null;
  };
  const backdropInputProps = {
    onWheel: onBackdropWheel,
    onTouchStart: onBackdropTouchStart,
    onTouchMove: onBackdropTouchMove,
    onTouchEnd: onBackdropTouchEnd,
    onTouchCancel: onBackdropTouchEnd,
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (isTour && ftue.isDeepDive) ftue.exitDeepDive();
        else if (isTour) ftue.skipTour();
        else ftue.closeHelp();
      } else if (isTour && e.key === 'ArrowRight') {
        e.preventDefault();
        ftue.nextStep();
      } else if (isTour && e.key === 'ArrowLeft') {
        e.preventDefault();
        ftue.prevStep();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ftue, isTour]);

  useEffect(() => {
    calloutRef.current?.focus();
  }, [target?.id, ftue.stepIndex, ftue.journeyId]);

  if (!target) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const isLast = isTour && ftue.stepIndex >= ftue.stepCount - 1;
  const showStarter = isLast && !ftue.isDeepDive && ftue.journeyId === 'full';

  return (
    <div className="ftue-spotlight-layer" role="presentation">
      <svg className="ftue-mask" width={vw} height={vh} aria-hidden>
        <defs>
          <mask id="ftue-hole-mask">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {hole && (
              <rect
                x={hole.left}
                y={hole.top}
                width={hole.width}
                height={hole.height}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={vw}
          height={vh}
          fill="rgba(8, 22, 25, 0.55)"
          mask="url(#ftue-hole-mask)"
        />
        {hole && (
          <rect
            x={hole.left}
            y={hole.top}
            width={hole.width}
            height={hole.height}
            rx="10"
            className="ftue-hole-ring"
          />
        )}
      </svg>

      {isTour && hole && (
        <>
          <div
            className="ftue-block-strip"
            style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }}
            aria-hidden
            {...backdropInputProps}
          />
          <div
            className="ftue-block-strip"
            style={{
              top: hole.bottom,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            aria-hidden
            {...backdropInputProps}
          />
          <div
            className="ftue-block-strip"
            style={{
              top: hole.top,
              left: 0,
              width: Math.max(0, hole.left),
              height: hole.height,
            }}
            aria-hidden
            {...backdropInputProps}
          />
          <div
            className="ftue-block-strip"
            style={{
              top: hole.top,
              left: hole.right,
              right: 0,
              height: hole.height,
            }}
            aria-hidden
            {...backdropInputProps}
          />
        </>
      )}
      {isTour && !hole && (
        <div
          className="ftue-block-strip"
          style={{ inset: 0 }}
          aria-hidden
          {...backdropInputProps}
        />
      )}

      <div
        ref={calloutRef}
        className={`ftue-callout${layout ? ` is-${layout.placement}` : ''}`}
        style={
          layout
            ? {
                top: layout.top,
                left: layout.left,
                maxWidth: layout.maxWidth,
              }
            : { top: 24, left: 24, maxWidth: 320 }
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="ftue-callout-title"
        aria-describedby="ftue-callout-body ftue-callout-capabilities"
        tabIndex={-1}
      >
        <FtueBlueprint kind={target.diagram} className="ftue-callout-bp" />
        <div className="ftue-callout-copy">
          {isTour && (
            <div className="ftue-progress" aria-live="polite">
              {ftue.isDeepDive
                ? t('overlay.deepDiveProgress', {
                    current: ftue.stepIndex + 1,
                    total: ftue.stepCount,
                  })
                : t('overlay.progress', {
                    label:
                      (ftue.journeyId
                        ? resolveFtueJourneyLabel(ftue.journeyId)
                        : null) ?? t('overlay.tour'),
                    current: ftue.stepIndex + 1,
                    total: ftue.stepCount,
                  })}
            </div>
          )}
          <h3 id="ftue-callout-title">{target.title}</h3>
          <p id="ftue-callout-body">{target.body}</p>
          {anchorUnavailable && (
            <p className="ftue-anchor-status" role="status" aria-live="polite">
              {t('overlay.anchorUnavailable')}
            </p>
          )}
          <div
            id="ftue-callout-capabilities"
            className="ftue-capabilities"
          >
            <strong>{t('overlay.whatYouCanDo')}</strong>
            <ul>
              {target.actions.map(action => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          </div>
          {showStarter && (
            <p className="ftue-callout-extra">
              {t('overlay.tryAsking')}{' '}
              <button
                type="button"
                className="ftue-link"
                onClick={() => onAskStarter?.(ftue.starterPrompt)}
              >
                {ftue.starterPrompt}
              </button>
            </p>
          )}
        </div>
        <div className="ftue-callout-actions">
          {isTour ? (
            <>
              {ftue.isDeepDive ? (
                <button
                  type="button"
                  className="ftue-btn ghost"
                  onClick={ftue.exitDeepDive}
                >
                  {t('overlay.backToOverview')}
                </button>
              ) : (
                <button
                  type="button"
                  className="ftue-btn ghost"
                  onClick={ftue.skipTour}
                >
                  {t('overlay.skip')}
                </button>
              )}
              <div className="ftue-callout-nav">
                {ftue.canDeepDive && (
                  <button
                    type="button"
                    className="ftue-btn ghost"
                    onClick={ftue.enterDeepDive}
                  >
                    {t('overlay.deepDive')}
                  </button>
                )}
                <button
                  type="button"
                  className="ftue-btn ghost"
                  onClick={ftue.prevStep}
                  disabled={ftue.stepIndex === 0 && !ftue.isDeepDive}
                >
                  {t('overlay.back')}
                </button>
                <button
                  type="button"
                  className="ftue-btn primary"
                  onClick={ftue.nextStep}
                >
                  {isLast
                    ? ftue.isDeepDive
                      ? t('overlay.backToOverview')
                      : t('overlay.done')
                    : t('overlay.next')}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="ftue-btn ghost"
              onClick={() => {
                ftue.openContextual();
              }}
            >
              {t('overlay.closeSpotlight')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FtueHelpPanel({ ftue }: { ftue: FtueController }) {
  const { t } = useTranslation('ftue');
  const [query, setQuery] = useState('');
  const [chromeOpen, setChromeOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const isCatalog = ftue.phase === 'catalog';
  const catalogHits = searchFtueCatalog(query);
  const surfaceLabel = resolveFtueSurfaceLabel(ftue.activeSurface);

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('button, input')?.focus();
  }, [isCatalog]);

  useFocusTrap(panelRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        ftue.closeHelp();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ftue]);

  return (
    <aside
      ref={panelRef}
      className="ftue-help-panel"
      role="dialog"
      aria-modal="true"
      aria-label={t('overlay.help')}
    >
      <div className="ftue-help-head">
        <div className="ftue-help-title-row">
          <Icon name="helpCircle" size={16} />
          <strong>{t('overlay.help')}</strong>
          <span className="ftue-help-surface">{surfaceLabel}</span>
        </div>
        <button
          type="button"
          className="ftue-icon-btn"
          aria-label={t('overlay.closeHelp')}
          onClick={ftue.closeHelp}
        >
          <Icon name="close" size={14} />
        </button>
      </div>

      <div className="ftue-help-actions">
        {ftue.canResume ? (
          <button
            type="button"
            className="ftue-btn primary"
            onClick={ftue.resumeTour}
          >
            {t('overlay.resumeTour')}
          </button>
        ) : (
          <>
            {ftue.surfaceTourAvailable && (
              <button
                type="button"
                className="ftue-btn primary"
                onClick={ftue.startSurfaceTour}
              >
                {t('overlay.tourThisScreen')}
              </button>
            )}
            <button
              type="button"
              className={`ftue-btn ${ftue.surfaceTourAvailable ? 'ghost' : 'primary'}`}
              onClick={() => ftue.startFullTour('help')}
            >
              {t('overlay.fullProductTour')}
            </button>
          </>
        )}
        <button
          type="button"
          className={`ftue-btn ghost${isCatalog ? ' on' : ''}`}
          onClick={() =>
            isCatalog ? ftue.openContextual() : ftue.openCatalog()
          }
        >
          {isCatalog ? t('overlay.thisScreen') : t('overlay.exploreAll')}
        </button>
        {ftue.activeSurface === 'assistant' && (
          <button
            type="button"
            className="ftue-btn ghost"
            onClick={() => {
              ftue.closeHelp();
              requestMaitreIntro();
            }}
          >
            {t('overlay.showIntroAgain')}
          </button>
        )}
      </div>

      {isCatalog ? (
        <>
          <label className="ftue-search">
            <Icon name="search" size={14} />
            <input
              type="search"
              placeholder={t('overlay.searchPlaceholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>
          <ul className="ftue-target-list">
            {catalogHits.map(hit => (
              <li key={hit.id}>
                <button
                  type="button"
                  className="ftue-target-item"
                  onClick={() => ftue.focusTarget(hit.id)}
                >
                  <span className="ftue-target-item-title">{hit.title}</span>
                  <span className="ftue-target-item-meta">
                    {hit.surfaceLabel}
                    {hit.catalogOnly ? t('overlay.notOnScreen') : ''}
                  </span>
                  <span className="ftue-target-item-body">{hit.body}</span>
                  <span className="ftue-target-item-action">
                    <strong>{t('overlay.useItTo')}</strong> {hit.actions[0]}
                  </span>
                  {hit.catalogOnly && hit.catalogNote ? (
                    <span className="ftue-target-item-note">
                      {hit.catalogNote}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <p className="ftue-help-lead">{t('overlay.onThisScreen')}</p>
          <ul className="ftue-target-list">
            {ftue.contentTargets.map(target => (
              <li key={target.id}>
                <button
                  type="button"
                  className="ftue-target-item"
                  onClick={() => ftue.focusTarget(target.id)}
                >
                  <span className="ftue-target-item-title">{target.title}</span>
                  <span className="ftue-target-item-body">{target.body}</span>
                  <span className="ftue-target-item-action">
                    <strong>{t('overlay.useItTo')}</strong> {target.actions[0]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {ftue.chromeTargets.length > 0 && (
            <div className="ftue-help-chrome">
              <button
                type="button"
                className="ftue-chrome-toggle"
                aria-expanded={chromeOpen}
                onClick={() => setChromeOpen(v => !v)}
              >
                {t('overlay.controlsEverywhere')}
                <span aria-hidden>{chromeOpen ? '−' : '+'}</span>
              </button>
              {chromeOpen && (
                <ul className="ftue-target-list">
                  {ftue.chromeTargets.map(target => (
                    <li key={target.id}>
                      <button
                        type="button"
                        className="ftue-target-item"
                        onClick={() => ftue.focusTarget(target.id)}
                      >
                        <span className="ftue-target-item-title">
                          {target.title}
                        </span>
                        <span className="ftue-target-item-body">
                          {target.body}
                        </span>
                        <span className="ftue-target-item-action">
                          <strong>{t('overlay.useItTo')}</strong>{' '}
                          {target.actions[0]}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
